<template>
  <div
    class="fixed top-0 left-0 w-full h-full overflow-y-auto z-[123432143234321] flex items-start justify-center bg-black/50 backdrop-blur-sm p-4"
    @click="showShuffleRainModal(false)"
  >
    <div
      class="bg-white w-full max-w-[1600px] max-h-[95dvh] overflow-hidden rounded-2xl shadow-2xl flex flex-col"
      @click.stop
    >
      <div
        class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-white"
      >
        <div class="flex items-center gap-4">
          <div
            class="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600"
          >
            <i class="fa-solid fa-shuffle text-xl"></i>
          </div>
          <h2 class="text-xl font-bold text-gray-800">Change Rain Events</h2>
        </div>
        <button
          @click="showShuffleRainModal(false)"
          :disabled="loading"
          class="p-2 rounded-full hover:bg-gray-100 text-gray-400 transition-colors disabled:opacity-50"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        <div
          class="bg-blue-50 border border-blue-100 rounded-xl p-2.5 transition-all"
        >
          <div class="flex justify-between items-start">
            <div class="flex gap-3">
              <div class="text-blue-600 mt-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="10" stroke-width="2" />
                  <path
                    d="M12 16V12M12 8H12.01"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div class="banner-content">
                <h3 class="font-semibold text-blue-900">Smart Shuffle</h3>
                <p
                  v-show="!collapsed"
                  class="text-sm text-blue-800/80 mt-1 leading-relaxed"
                >
                  The shuffle redistributes rainfall across 24 hours while
                  preserving the average duration. Once new intervals are
                  generated, they can be edited. The end time is automatically
                  calculated. If this exceeds 23:59, the start time is adjusted.
                </p>
              </div>
            </div>
            <button
              @click="collapsed = !collapsed"
              class="text-blue-600 hover:bg-blue-100 p-1 rounded transition-colors"
            >
              <i
                :class="collapsed ? 'bi bi-chevron-down' : 'bi bi-chevron-up'"
              ></i>
            </button>
          </div>

          <div
            v-show="!collapsed"
            class="mt-2 pt-2 border-t border-blue-200/50"
          >
            <div
              v-if="shuffledRains.length"
              class="grid grid-cols-2 md:grid-cols-3 gap-2"
            >
              <div class="flex flex-col">
                <span
                  class="text-[10px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1"
                >
                  <HashtagIcon class="w-3 h-3" /> Campaign ID
                </span>
                <span class="text-sm font-medium text-gray-700">{{
                  shuffledRains[0].campaign_id
                }}</span>
              </div>
              <div class="flex flex-col">
                <span
                  class="text-[10px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1"
                >
                  <CalendarIcon class="w-3 h-3" /> Day
                </span>
                <span class="text-sm font-medium text-gray-700">{{
                  extractDatePart(shuffledRains[0].start_at)
                }}</span>
              </div>
              <div class="flex flex-col">
                <span
                  class="text-[10px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-1"
                >
                  <ClockIcon class="w-3 h-3" /> Duration (Avg)
                </span>
                <span class="text-sm font-medium text-gray-700"
                  >{{ getDuration(shuffledRains[0]) }} min</span
                >
              </div>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 border border-gray-200 rounded-xl p-2.5">
          <span class="block text-sm font-bold text-gray-700 mb-4"
            >Choose custom time range</span
          >
          <div class="flex flex-wrap items-center gap-6">
            <div class="flex items-center gap-3">
              <label for="start-time" class="text-sm font-medium text-gray-600"
                >Start Time</label
              >
              <input
                id="start-time"
                type="time"
                v-model="startTimeRange"
                :disabled="loading"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2"
              />
            </div>
            <div class="flex items-center gap-3">
              <label for="end-time" class="text-sm font-medium text-gray-600"
                >End Time</label
              >
              <input
                id="end-time"
                type="time"
                v-model="endTimeRange"
                :disabled="loading"
                class="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2"
              />
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          <div
            v-for="(rain, index) in rainsToShuffle"
            :key="'comparison-' + (rain._id || index)"
            class="group bg-white border border-gray-200 rounded-xl p-2.5 hover:border-blue-300 transition-colors shadow-sm"
          >
            <h4 class="text-xs font-bold text-slate-500 uppercase mb-3">
              #{{ index + 1 }} Rain Event
            </h4>

            <div class="flex flex-col md:flex-row items-center gap-2">
              <div class="flex-1 w-full p-2 rounded-lg bg-gray-50/50">
                <span class="text-[10px] font-bold text-slate-500  block mb-1"
                  >BEFORE</span
                >
                <div class="text-sm font-semibold text-gray-600">
                  {{ formatTime(rain.start_at) }} —
                  {{ formatTime(rain.end_at) }}
                  <span class="text-[10px] opacity-60">UTC</span>
                </div>
              </div>

            <div class="text-gray-300 group-hover:text-blue-400 transition-colors transform rotate-90 md:rotate-0">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M5 12H19M19 12L12 5M19 12L12 19"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>

              <div
                class="flex-1 w-full p-2 rounded-lg bg-blue-50/30 border border-blue-100"
              >
                <span class="text-[10px] font-bold text-blue-500 block mb-1"
                  >AFTER (EDITABLE)</span
                >

                <div
                  v-if="shuffledRains[index]"
                  class="flex items-center justify-between gap-4"
                >
                  <input
                    type="time"
                    v-model="shuffledRains[index].start_at_local"
                    @input="updateEndTime(index)"
                    :disabled="loading"
                    class="bg-white border border-blue-200 text-gray-900 text-sm font-bold rounded-md focus:ring-blue-500 focus:border-blue-500 p-1 px-2"
                  />
                  <div class="text-sm font-bold text-blue-600">
                    {{ formatTime(shuffledRains[index].end_at) }}
                    <span class="text-[10px] opacity-60">UTC</span>
                  </div>
                </div>

                <div
                  v-else
                  class="flex items-center gap-2 text-gray-400 animate-pulse"
                >
                  <div
                    class="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"
                  ></div>
                  <span class="text-xs">Generating...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-nowrap gap-3 justify-end"
      >
        <button
          @click="shuffleRains"
          :disabled="!rainsToShuffle.length || loading"
          class="px-3 py-2.5 rounded-xl flex-1 md:flex-none border border-gray-300 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <i class="fa-solid fa-random text-blue-500"></i>
          Shuffle Again
        </button>

        <button
          @click="confirmSave"
          :disabled="loading"
          class="px-3 py-2.5 rounded-xl flex-1 md:flex-none bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 active:scale-95 transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
        >
          <template v-if="!loading">
            <i class="fa-solid fa-cloud-arrow-up"></i>
            Save Changes
          </template>

          <template v-else>
            <Loader class="!w-4 !h-4" />
            Saving...
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// I componenti e la logica rimangono identici a quelli forniti
import { HashtagIcon } from "@heroicons/vue/24/outline";
import { postFetch } from "@/api/axiosClient";
import Swal from "sweetalert2";
import { CalendarIcon, ClockIcon } from "lucide-vue-next";
import Loader from "../Loader.vue";

export default {
  name: "RainsShuffleModal",
  props: {
    rainsToShuffle: {
      type: Array,
      required: true,
    },
    showShuffleRainModal: Function,
  },
  components: {
    HashtagIcon,
    ClockIcon,
    CalendarIcon,
    Loader
  },
  data() {
    return {
      shuffledRains: [],
      loading: false,
      collapsed: false,
      startTimeRange: "00:00",
      endTimeRange: "23:59",
    };
  },
  mounted() {
    this.shuffleRains();
  },
  methods: {
    pad(n) {
      return n < 10 ? "0" + n : n;
    },
    getDuration(rain) {
      const start = new Date(rain.start_at);
      const end = new Date(rain.end_at);
      return Math.round((end - start) / 60000);
    },
    extractDatePart(datetimeStr) {
      return datetimeStr.split(" ")[0];
    },
    fmtBackend(date) {
      return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(
        date.getDate(),
      )} ${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(
        date.getSeconds(),
      )}`;
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    updateEndTime(index) {
      const item = this.shuffledRains[index];
      const originalDate = this.extractDatePart(item.start_at);
      const [hour, minute] = item.start_at_local.split(":").map(Number);

      if (isNaN(hour) || isNaN(minute)) return;

      const newStartStr = `${originalDate} ${this.pad(hour)}:${this.pad(
        minute,
      )}:00`;
      const newStart = new Date(newStartStr);

      const durationMs = item.duration * 60000;
      const newEnd = new Date(newStart.getTime() + durationMs);

      const maxEnd = new Date(`${originalDate} 23:59:59`);

      if (newEnd > maxEnd) {
        Swal.fire({
          icon: "warning",
          title: "Invalid time",
          text: "The selected start time would cause the rain to end after 23:59. Please choose an earlier time.",
        });

        item.start_at_local = `${this.pad(
          new Date(item.start_at).getHours(),
        )}:${this.pad(new Date(item.start_at).getMinutes())}`;

        return;
      }

      item.start_at = this.fmtBackend(newStart);
      item.end_at = this.fmtBackend(newEnd);
      item.start_at_local = `${this.pad(newStart.getHours())}:${this.pad(
        newStart.getMinutes(),
      )}`;
    },
    shuffleRains() {
      if (!this.rainsToShuffle.length) {
        this.shuffledRains = [];
        return;
      }

      const durations = this.rainsToShuffle.map((r) => {
        const start = new Date(r.start_at);
        const end = new Date(r.end_at);
        return (end - start) / 60000;
      });

      const avgDuration =
        durations.reduce((a, b) => a + b, 0) / durations.length;
      const count = this.rainsToShuffle.length;

      const [startHour, startMinute] = this.startTimeRange
        .split(":")
        .map(Number);
      const [endHour, endMinute] = this.endTimeRange.split(":").map(Number);

      const baseDate = new Date(this.rainsToShuffle[0].start_at);
      baseDate.setHours(0, 0, 0, 0);

      const rangeStart = new Date(baseDate.getTime());
      rangeStart.setHours(startHour, startMinute, 0, 0);

      const rangeEnd = new Date(baseDate.getTime());
      rangeEnd.setHours(endHour, endMinute, 59, 999);

      if (rangeStart >= rangeEnd) {
        Swal.fire({
          icon: "error",
          title: "Invalid time range",
          text: "The start time must be earlier than the end time.",
        });
        return;
      }

      const totalRangeMinutes = (rangeEnd - rangeStart) / 60000;
      const requiredTotal = avgDuration * count;

      if (requiredTotal > totalRangeMinutes) {
        const hours = Math.floor(requiredTotal / 60);
        const minutes = Math.round(requiredTotal % 60);

        let timeStr = "";
        if (hours > 0) timeStr += `${hours} hour${hours > 1 ? "s" : ""} `;
        if (minutes > 0)
          timeStr += `${minutes} minute${minutes > 1 ? "s" : ""}`;
        if (timeStr === "") timeStr = "less than 1 minute";

        Swal.fire({
          icon: "error",
          title: "Insufficient time range",
          text: `You need at least ${timeStr.trim()} to distribute ${count} rains with an average duration of ${Math.round(
            avgDuration,
          )} minutes.`,
        });
        return;
      }

      const spacing = (totalRangeMinutes - avgDuration) / (count - 1);
      const newRains = [];

      for (let i = 0; i < count; i++) {
        const slotStart = spacing * i;
        const slotEnd = slotStart + spacing;

        const maxOffset = Math.max(slotEnd - slotStart - avgDuration, 0);
        const randomOffset = Math.random() * Math.min(maxOffset, 30);

        let start = new Date(
          rangeStart.getTime() + (slotStart + randomOffset) * 60000,
        );
        let end = new Date(start.getTime() + avgDuration * 60000);

        if (end > rangeEnd) {
          end = new Date(rangeEnd.getTime());
          start = new Date(end.getTime() - avgDuration * 60000);
        }

        newRains.push({
          ...this.rainsToShuffle[i],
          start_at: this.fmtBackend(start),
          end_at: this.fmtBackend(end),
          duration: Math.round(avgDuration),
          start_at_local: `${this.pad(start.getHours())}:${this.pad(
            start.getMinutes(),
          )}`,
        });
      }

      this.shuffledRains = newRains;
    },

    toBackendISO(datetimeStr) {
      const [datePart, timePart] = datetimeStr.split(" ");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, minute, second] = timePart.split(":").map(Number);

      const date = new Date(
        Date.UTC(year, month - 1, day, hour, minute, second),
      );

      return date.toISOString().replace("Z", "+00:00");
    },
    async confirmSave() {
      if (!this.shuffledRains.length) return;

      const totalRains = this.shuffledRains.length;

      const result = await Swal.fire({
        title: `Saving ${totalRains} rains.`,
        text: `Are you sure you want to save these shuffled rains?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, save",
        confirmButtonColor: "#2563eb", // blue-600
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      try {
        this.loading = true;
        const dataToSend = this.shuffledRains.map((item) => ({
          ...item,
          start_at: this.toBackendISO(item.start_at),
          end_at: this.toBackendISO(item.end_at),
        }));

        await postFetch("freebets/updateRainInterval", dataToSend);

        await Swal.fire({
          icon: "success",
          title: "Rains updated!",
          timer: 1500,
          showConfirmButton: false,
        });

        window.location.reload();
      } catch (error) {
        console.error("Save failed:", error);
        Swal.fire({
          icon: "error",
          title: "Error saving rains",
          text: "Something went wrong. Please try again.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
